import { action, computed, makeObservable, observable } from 'mobx'
import { getValue, isPending, trackedAction } from 'mobx-async'
import { computedRouteParam } from '@stores/computedRouteParam'

import { LeadSource } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { Home } from '@new/__global/components/HomeSelect/HomeSelect.types'
import {
  CreateBookingActionProps,
  CreateBookingFormProps,
} from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'
import { TapeChartCreateTapeStore } from '@new/domains/fastBookingsTapeChart/stores/TapeChartCreateTapeStore'

import { trackError, trackEvent } from '@/tracking/customEventReport'
import { arriere } from '@arriere/index'
import {
  createHotelBookingForExistingGuest,
  createHotelBookingForNewGuest,
} from '@arriere/modules/bookingsCreate'
import { getHomeCancellationPoliciesBySources } from '@arriere/modules/cancellationPolicies'
import { mountRouteUrl, ROUTES } from '@routes'
import { GuestSelectStore } from '@stores/GuestSelectStore'

import { BookingModificationStore } from '../../bookings/stores/BookingModificationStore'
import { BookingsTapeChartStore } from './BookingsTapeChartStore'
import { createBookingMembershipStore } from './CreateBookingMembershipStore'
import { TapeChartBlockStore } from './TapeChartBlockStore'

class _CreateBookingStore {
  bookingLeadSource: LeadSource = LeadSource.website
  createManualBookingError?: string
  createReservationObject?: CreateBookingFormProps
  homeId?: string
  isHotel?: boolean

  // Using computedRouteParam to identify hotelId as query string as is exclusive of hotelTapeChart route
  private hotelIdParam = computedRouteParam<string | undefined>('hotel-id', {
    patterns: [ROUTES.hotelTapeChart.path],
  })
  // Using computedRouteParam to identify hotelRoomTypeId as query string as is exclusive of hotelTapeChart route
  private roomTypeIdParam = computedRouteParam<string | undefined>('room-type-id', {
    patterns: [ROUTES.hotelTapeChart.path],
  })

  constructor() {
    makeObservable<_CreateBookingStore, '__ASYNC_home_cancellation_policy'>(this, {
      bookingLeadSource: observable,
      createManualBookingError: observable,
      createReservationObject: observable,
      homeId: observable,
      isHotel: observable,
      __ASYNC_home_cancellation_policy: computed,
      homeCancellationPolicy: computed,
      hotelId: computed,
      isLoadingSubmitCreateBooking: computed,
      roomTypeId: computed,
      setBookingLeadSource: action.bound,
      setCreateManualBookingError: action.bound,
      setCreateReservationObject: action.bound,
      setHomeId: action.bound,
      setIsHotel: action.bound,
      resetCreateReservationCachedFields: action.bound,
    })

    this.createManualBooking = trackedAction<any>(this.createManualBooking.bind(this))
    this.homesSimpleListWithNoRestrictions = trackedAction<any>(
      this.homesSimpleListWithNoRestrictions.bind(this),
    )
    this.homesSimpleList = trackedAction<any>(this.homesSimpleList.bind(this))
  }

  get hotelId() {
    return this.hotelIdParam?.get()
  }

  get roomTypeId() {
    return this.roomTypeIdParam?.get()
  }

  private get __ASYNC_home_cancellation_policy() {
    return this.homeId
      ? getHomeCancellationPoliciesBySources({
          homeId: this.homeId,
          sources: [this.bookingLeadSource],
        }).then(res => res.cancellationPolicies[0])
      : Promise.resolve(undefined)
  }

  get homeCancellationPolicy() {
    return getValue(this.__ASYNC_home_cancellation_policy)
  }

  get isLoadingSubmitCreateBooking() {
    return isPending(this.createManualBooking)
  }

  setBookingLeadSource = (bookingLeadSource: LeadSource) => {
    this.bookingLeadSource = bookingLeadSource
  }

  setCreateReservationObject = (data?: CreateBookingFormProps) => {
    this.createReservationObject = data
  }

  setCreateManualBookingError = (error?: string) => {
    this.createManualBookingError = error
  }

  setHomeId = (homeId: string) => {
    this.homeId = homeId
  }

  setIsHotel = (isHotel?: boolean) => {
    this.isHotel = isHotel
  }

  resetCreateReservationCachedFields = () => {
    this.createReservationObject = undefined
    TapeChartBlockStore.toggleCreateTapeBlockModal = false
    BookingModificationStore.setSelectedHomeRestrictions(undefined)
    BookingsTapeChartStore.propertyCalendarStayLimits = undefined
    createBookingMembershipStore.reset()

    // Clear the tape chart selection so the next creation starts fresh. Multi-property
    // selections are kept intentionally for the quotes flow, so only clear single ones.
    const hasMultipleSelection = (TapeChartCreateTapeStore.createTapeContext?.length ?? 0) > 1
    if (!hasMultipleSelection) {
      TapeChartCreateTapeStore.resetCreateTapeContext()
    }
  }

  async homesSimpleList(query: string): Promise<Home[]> {
    const fromDate = this.createReservationObject?.period?.fromDate
    const toDate = this.createReservationObject?.period?.toDate

    return fromDate && toDate
      ? arriere.propertiesList.getHomesSimpleList(query, fromDate, toDate)
      : ([] as Home[])
  }

  async homesSimpleListWithNoRestrictions(query: string): Promise<Home[]> {
    return arriere.propertiesList.getHomesListNoRestrictions(query)
  }

  async createManualBooking({
    isHotelContext,
    bookingRequestInfo,
    guestId,
    guestData,
    onSuccessAction,
    shouldOpenBookingInfoTab,
  }: CreateBookingActionProps & {
    isHotelContext?: boolean
    onSuccessAction?: (bookingId: string) => void
    shouldOpenBookingInfoTab?: boolean
  }): Promise<void> {
    const onSuccessActivation = (hash: string, id: string) => {
      onSuccessAction?.(id)
      this.setCreateManualBookingError(undefined)
      Alert.Success('Booking was created successfully')
      if (shouldOpenBookingInfoTab) {
        window.open(
          mountRouteUrl(ROUTES.bookingInfo.path, { hash }),
          '_blank',
          'noreferrer noopener',
        )
      }
    }

    const handleBookingResponse = ({ data, errors }: { data?: any; errors?: any }) => {
      if (data?.hash) {
        onSuccessActivation(data?.hash, data?.id)
        trackEvent('Create booking: reservation successfully created!', {
          ...bookingRequestInfo,
          guestId,
          guestData,
        })
      }
      if (errors) {
        this.setCreateManualBookingError(errors[0]?.message)
        trackError('Create booking: There was an error while creating a reservation', {
          ...bookingRequestInfo,
          guestId,
          guestData,
          errors,
        })
        Alert.Error(errors[0].message)
      }
    }

    const createBookingForGuest = () => {
      if (guestId) {
        return isHotelContext
          ? createHotelBookingForExistingGuest({ bookingRequestInfo, guestId })
          : arriere.bookingsCreate.createBookingForExistingGuest({ bookingRequestInfo, guestId })
      }

      if (guestData) {
        return isHotelContext
          ? createHotelBookingForNewGuest({ bookingRequestInfo, guestData })
          : arriere.bookingsCreate.createBookingForNewGuest({ bookingRequestInfo, guestData })
      }

      return Promise.resolve(undefined)
    }

    try {
      const response = await createBookingForGuest()
      if (response) handleBookingResponse(response)
    } catch (error) {
      trackError('Create booking: There was an error while creating a reservation', {
        ...bookingRequestInfo,
        guestId,
        guestData,
        error,
      })
      Alert.Error('Oh no! An error occurred while creating a reservation.')
    }
  }
}

export const CreateBookingStore = new _CreateBookingStore()
